import type {
    AppModule,
    AppSubmodule,
    ExposedMenu,
    MenuGroup,
    SideLink,
} from '@moniebook-web/components/widgets/sidebar';
import { ArrowUpDownLine } from '@moniebook-web/icons/arrows/arrow-up-down-line';
import { BankFill } from '@moniebook-web/icons/buildings/bank-fill';
import { BankLine } from '@moniebook-web/icons/buildings/bank-line';
import { File4Line } from '@moniebook-web/icons/document/file-4-line';
import { FileEditLine } from '@moniebook-web/icons/document/file-edit-line';
import { Accounting } from '@moniebook-web/icons/sidebar-rail/accounting';
import {
    ApplicationModule,
    MENU_PARENTS,
    MENU_SECTIONS,
} from '@moniebook-web/types';

const ACCOUNTING_SIDEBAR_COLOR = '#6EA31B';

const FEATURE_FLAGS = {
    ENABLE_CONNECT_ACCOUNTS_PAGE: 'moniebook.pos.enableConnectAccountsPage',
    ENABLE_OTHER_ACCOUNTS_PAGE: 'moniebook.pos.enableOtherAccountsPage',
    ENABLE_TRANSACTIONS_PAGE: 'moniebook.pos.enableTransactionsPage',
    ENABLE_CHART_OF_ACCOUNT_PAGE: 'moniebook.pos.enableChartOfAccountPage',
    ENABLE_BILLS_PAGE: 'moniebook.pos.enableBillsPage',
    ENABLE_PROFIT_AND_LOSS_PAGE: 'moniebook.pos.enableProfitAndLossPage',
    ENABLE_CATEGORIES_PAGE: 'moniebook.pos.enableCategoriesPage',
} as const;

const CONNECTED_ACCOUNTS_PERMISSIONS = [
    'MANAGE_CONNECTED_ACCOUNT',
    'VIEW_CONNECTED_ACCOUNT',
    'CONNECT_ACCOUNT',
];
const OTHER_ACCOUNTS_PERMISSIONS = [
    'VIEW_OTHER_ACCOUNTS',
    'MANAGE_OTHER_ACCOUNTS',
];
const TRANSACTIONS_PERMISSIONS = [
    'VIEW_TRANSACTION',
    'UPDATE_TRANSACTION',
    'REVIEW_TRANSACTION',
    'CATEGORIZE_TRANSACTION',
    'EXPORT_TRANSACTION',
    'CREATE_MANUAL_TRANSACTION',
    'DELETE_MANUAL_TRANSACTION',
];
const CHART_OF_ACCOUNT_PERMISSIONS = ['VIEW_ACCOUNTS', 'MANAGE_ACCOUNTS'];
const BILLS_PERMISSIONS: string[] = [];
const PROFIT_AND_LOSS_PERMISSIONS = ['VIEW_PL_REPORT'];
const CATEGORY_PERMISSIONS = ['VIEW_ACCOUNTS', 'MANAGE_ACCOUNTS'];

export const accountingMenu: ExposedMenu = {
    menuGroups: [
        {
            id: MENU_PARENTS.ACCOUNTING,
            name: 'Accounting',
            order: 5,
            icon: <BankFill />,
            sectionId: MENU_SECTIONS.NONE,
            color: ACCOUNTING_SIDEBAR_COLOR,
        },
    ] as MenuGroup[],
    submenu: [
        {
            sectionId: MENU_SECTIONS.NONE,
            parentId: MENU_PARENTS.ACCOUNTING,
            order: 1,
            accessCodeList: CONNECTED_ACCOUNTS_PERMISSIONS,
            name: 'Connected accounts',
            to: '/dashboard/accounts',
            grantAccessIfAny: true,
            featureFlag: FEATURE_FLAGS.ENABLE_CONNECT_ACCOUNTS_PAGE,
        },
        {
            sectionId: MENU_SECTIONS.NONE,
            parentId: MENU_PARENTS.ACCOUNTING,
            order: 2,
            accessCodeList: OTHER_ACCOUNTS_PERMISSIONS,
            name: 'Other Accounts',
            to: '/dashboard/other-accounts',
            grantAccessIfAny: true,
            featureFlag: FEATURE_FLAGS.ENABLE_OTHER_ACCOUNTS_PAGE,
        },
        {
            sectionId: MENU_SECTIONS.NONE,
            parentId: MENU_PARENTS.ACCOUNTING,
            order: 3,
            accessCodeList: TRANSACTIONS_PERMISSIONS,
            name: 'Transactions',
            to: '/dashboard/transactions',
            grantAccessIfAny: true,
            featureFlag: FEATURE_FLAGS.ENABLE_TRANSACTIONS_PAGE,
        },
        {
            sectionId: MENU_SECTIONS.NONE,
            parentId: MENU_PARENTS.ACCOUNTING,
            order: 4,
            accessCodeList: CHART_OF_ACCOUNT_PERMISSIONS,
            name: 'Chart of Account',
            to: '/dashboard/chart-of-account',
            grantAccessIfAny: true,
            featureFlag: FEATURE_FLAGS.ENABLE_CHART_OF_ACCOUNT_PAGE,
        },
        {
            sectionId: MENU_SECTIONS.NONE,
            parentId: MENU_PARENTS.ACCOUNTING,
            order: 5,
            accessCodeList: BILLS_PERMISSIONS,
            name: 'Bills',
            to: '/dashboard/bills',
            grantAccessIfAny: true,
            featureFlag: FEATURE_FLAGS.ENABLE_BILLS_PAGE,
        },
        {
            sectionId: MENU_SECTIONS.NONE,
            parentId: ApplicationModule.ACCOUNTING,
            order: 6,
            accessCodeList: PROFIT_AND_LOSS_PERMISSIONS,
            name: 'Profit and Loss',
            to: '/dashboard/profit-and-loss',
            grantAccessIfAny: true,
            featureFlag: FEATURE_FLAGS.ENABLE_PROFIT_AND_LOSS_PAGE,
        },
        {
            sectionId: MENU_SECTIONS.NONE,
            parentId: MENU_PARENTS.ACCOUNTING,
            order: 7,
            accessCodeList: CATEGORY_PERMISSIONS,
            name: 'Category',
            to: '/dashboard/accounting-categories',
            grantAccessIfAny: true,
            featureFlag: FEATURE_FLAGS.ENABLE_CATEGORIES_PAGE,
        },
    ] as SideLink[],
    modules: [
        {
            id: ApplicationModule.ACCOUNTING,
            name: 'Accounting',
            order: 5,
            icon: <Accounting />,
            backgroundColor: '#F7DCCF',
            foregroundColor: '#F07339',
        },
    ] as AppModule[],
    subModules: [
        {
            parentId: ApplicationModule.ACCOUNTING,
            order: 1,
            accessCodeList: CONNECTED_ACCOUNTS_PERMISSIONS,
            name: 'Accounts',
            to: '/dashboard/accounts',
            grantAccessIfAny: true,
            featureFlag: FEATURE_FLAGS.ENABLE_CONNECT_ACCOUNTS_PAGE,
            icon: <BankLine />,
        },
        {
            parentId: ApplicationModule.ACCOUNTING,
            order: 2,
            accessCodeList: OTHER_ACCOUNTS_PERMISSIONS,
            name: 'Other Accounts',
            to: '/dashboard/other-accounts',
            grantAccessIfAny: true,
            featureFlag: FEATURE_FLAGS.ENABLE_OTHER_ACCOUNTS_PAGE,
            icon: <BankLine />,
        },
        {
            parentId: ApplicationModule.ACCOUNTING,
            order: 3,
            accessCodeList: TRANSACTIONS_PERMISSIONS,
            name: 'Transactions',
            to: '/dashboard/transactions',
            grantAccessIfAny: true,
            featureFlag: FEATURE_FLAGS.ENABLE_TRANSACTIONS_PAGE,
            icon: <ArrowUpDownLine />,
        },
        {
            parentId: ApplicationModule.ACCOUNTING,
            order: 4,
            accessCodeList: CHART_OF_ACCOUNT_PERMISSIONS,
            name: 'Chart of accounts',
            to: '/dashboard/chart-of-account',
            grantAccessIfAny: true,
            featureFlag: FEATURE_FLAGS.ENABLE_CHART_OF_ACCOUNT_PAGE,
            icon: <FileEditLine />,
        },
        {
            parentId: ApplicationModule.ACCOUNTING,
            order: 5,
            accessCodeList: BILLS_PERMISSIONS,
            name: 'Bills',
            to: '/dashboard/bills',
            grantAccessIfAny: true,
            featureFlag: FEATURE_FLAGS.ENABLE_BILLS_PAGE,
            icon: <File4Line />,
        },
        {
            parentId: ApplicationModule.ACCOUNTING,
            order: 6,
            accessCodeList: PROFIT_AND_LOSS_PERMISSIONS,
            name: 'Profit and Loss',
            to: '/dashboard/profit-and-loss',
            grantAccessIfAny: true,
            featureFlag: FEATURE_FLAGS.ENABLE_PROFIT_AND_LOSS_PAGE,
            icon: <FileEditLine />,
        },
        {
            parentId: ApplicationModule.ACCOUNTING,
            order: 7,
            accessCodeList: CATEGORY_PERMISSIONS,
            name: 'Category',
            to: '/dashboard/accounting-categories',
            grantAccessIfAny: true,
            featureFlag: FEATURE_FLAGS.ENABLE_CATEGORIES_PAGE,
            icon: <FileEditLine />,
        },
    ] as AppSubmodule[],
};
